import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { Book, CheckCircle, Clock } from 'lucide-react';

const Frameworks = () => {
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFrameworks = async () => {
    const data = await apiFetch(`${API}/frameworks`);
    if (data?._error) {
      console.error('Error fetching frameworks:', data.message);
      setLoading(false);
      return;
    }
    setFrameworks(data);
    setLoading(false);
  };

   
  useEffect(() => {
    fetchFrameworks();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  const frameworkDescriptions = {
    ESRS: {
      fullName: 'European Sustainability Reporting Standards',
      compliance: 'Mandatory under CSRD',
      scope: 'E1-E5 (Environmental), S1-S4 (Social), G1 (Governance)',
      color: 'blue'
    },
    TCFD: {
      fullName: 'Task Force on Climate-related Financial Disclosures',
      compliance: 'Voluntary (recommended by regulators)',
      scope: 'Governance, Strategy, Risk Management, Metrics & Targets',
      color: 'green'
    },
    GRI: {
      fullName: 'Global Reporting Initiative',
      compliance: 'Voluntary global standard',
      scope: 'Universal Standards covering all ESG topics',
      color: 'orange'
    },
    SASB: {
      fullName: 'Sustainability Accounting Standards Board',
      compliance: 'Voluntary (sector-specific)',
      scope: 'Financially material topics for Financials sector',
      color: 'red'
    }
  };

  return (
    <div className="space-y-6" data-testid="frameworks-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Configuration</h1>
        <p className="text-slate-600">Multi-framework approach with crosswalk mappings</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {frameworks.map((framework) => {
          const info = frameworkDescriptions[framework.name];
          const colorClasses = {
            blue: 'border-blue-500 bg-blue-50',
            green: 'border-green-500 bg-green-50',
            orange: 'border-orange-500 bg-orange-50',
            red: 'border-red-500 bg-red-50'
          };
          
          return (
            <div key={framework.id} className={`bg-white rounded-lg shadow-lg border-l-4 ${colorClasses[info.color]} p-6`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">{framework.name}</h2>
                  <p className="text-sm text-slate-600 mt-1">{info.fullName}</p>
                </div>
                <Book className="text-slate-400" size={32} />
              </div>

              <div className="space-y-3 mb-4">
                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase">Compliance</span>
                  <p className="text-sm text-slate-700">{info.compliance}</p>
                </div>
                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase">Scope</span>
                  <p className="text-sm text-slate-700">{info.scope}</p>
                </div>
                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase">Version</span>
                  <p className="text-sm text-slate-700">{framework.version}</p>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-slate-700">Implementation Progress</span>
                  <span className="text-sm font-bold text-slate-900">{framework.coverage_percentage.toFixed(0)}%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all bg-${info.color}-600`}
                    style={{ width: `${framework.coverage_percentage}%` }}
                  ></div>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">
                  {framework.implemented_indicators} / {framework.total_indicators} indicators
                </span>
                {framework.coverage_percentage >= 75 ? (
                  <span className="flex items-center gap-1 text-green-600">
                    <CheckCircle size={16} /> On Track
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-orange-600">
                    <Clock size={16} /> In Progress
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Framework Integration Strategy</h2>
        <div className="prose max-w-none">
          <p className="text-slate-700 mb-4">
            This platform integrates <strong>four major ESG frameworks</strong> to demonstrate comprehensive regulatory coverage:
          </p>
          <ul className="list-disc list-inside space-y-2 text-slate-700">
            <li><strong>ESRS</strong>: Primary compliance driver under CSRD (mandatory for EU financial institutions)</li>
            <li><strong>TCFD</strong>: Climate risk disclosure alignment (regulatory expectation in multiple jurisdictions)</li>
            <li><strong>GRI</strong>: Universal reporting standard for sustainability transparency</li>
            <li><strong>SASB</strong>: Sector-specific financially material metrics for investors</li>
          </ul>
          <p className="text-slate-700 mt-4">
            Each KPI can be <strong>tagged to multiple frameworks</strong>, with crosswalk mappings showing how indicators align across standards.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Frameworks;